import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import AssignPropertiesModalSkeleton from '@/components/v2/molecules/admin/tabs/UserManagementTabs/PropertyOwners/AssignPropertiesModalSkeleton';
import { useProperties } from '@/hooks/useProperties';
import { useUserManagement } from '@/hooks/useUserManagement';
import { useEffect, useState } from 'react';

interface AssignPropertiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: any;
}

const AssignPropertiesModal = ({
  isOpen,
  onClose,
  onSuccess,
  user,
}: AssignPropertiesModalProps) => {
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);

  const { data: properties = [], isLoading: isLoadingProperties } =
    useProperties();

  const { assignProperties, loading: isAssigningProperties } =
    useUserManagement();

  useEffect(() => {
    if (user?.user_property_assignments) {
      const assignedIds = user.user_property_assignments.map(
        (assignment) => assignment.property_id,
      );

      setSelectedProperties(assignedIds);
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const { error } = await assignProperties(user.user_id, selectedProperties);

    if (!error) {
      onSuccess();
    }
  };

  const handlePropertyToggle = (propertyId: string, checked: boolean) => {
    if (checked) {
      setSelectedProperties((prev) => [...prev, propertyId]);
    } else {
      setSelectedProperties((prev) => prev.filter((id) => id !== propertyId));
    }
  };

  if (isLoadingProperties) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-lg">
          <AssignPropertiesModalSkeleton />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            Asignar Propiedades - {user.first_name} {user.last_name}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="max-h-96 overflow-y-auto space-y-3 custom-scrollbar-vertical">
            {properties.map((property) => {
              const locationDisplay = property.neighborhood
                ? `${property.neighborhood.city}, ${property.neighborhood.state}`
                : '';
              return (
                <div
                  key={property.id}
                  className="flex items-center space-x-3 p-3 border rounded-lg"
                >
                  <Checkbox
                    id={property.id}
                    checked={selectedProperties.includes(property.id)}
                    onCheckedChange={(checked) =>
                      handlePropertyToggle(property.id, checked as boolean)
                    }
                  />
                  <label
                    htmlFor={property.id}
                    className="flex-1 cursor-pointer"
                  >
                    <div className="font-medium text-gray-900">
                      {property.title}
                    </div>
                    <div className="text-sm text-gray-600">
                      {property.shortName}
                    </div>
                    <div className="text-sm text-gray-500">
                      {locationDisplay}
                    </div>
                  </label>
                </div>
              );
            })}
          </div>

          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-gray-600">
              {selectedProperties.length} de {properties.length} propiedades
              seleccionadas
            </div>

            <div className="flex space-x-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancelar
              </Button>

              <Button
                type="submit"
                className="bg-brand-primary hover:bg-brand-primary-hover"
                disabled={isAssigningProperties}
              >
                {isAssigningProperties
                  ? 'Guardando...'
                  : 'Guardar Asignaciones'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AssignPropertiesModal;
